import { useEffect, useRef } from 'react';
import { useJWTAuth } from '@crema/services/auth/JWTAuthProvider';
import {
  buildSignInPayloadFromUser,
  buildSignOutPayload,
  type AuthMeResponse,
} from '../../../utils/gtm/auth-events';
import { trackSignOut, trackUserProperty } from '../../../utils/gtm';

/**
 * 인증된 사용자 식별 정보를 GTM dataLayer 에 동기화한다.
 * - user.id 가 null → id 로 변화: user_property 발화
 * - user.id 가 id → null 로 변화: sign_out 발화
 * - user.id 가 id1 → id2 로 변화: sign_out 후 user_property 발화 (계정 전환)
 * - 동일 id 안에서의 user object 변경: 무시
 */
export function useTrackUserProperty(): void {
  const { user: rawUser } = useJWTAuth();
  // rawUser 의 정적 타입은 AuthUserType | UserType | undefined 이지만
  // 실측 응답 필드(snsType, gender, birthDate, marketingEmailAgreed 등)는
  // utils/gtm/auth-events.ts 의 AuthMeResponse 와 동일 모양이다.
  // 빌더가 인덱스 시그니처를 허용하므로 AuthMeResponse 로 안전하게 좁힌다.
  const user = rawUser as AuthMeResponse | undefined;
  const currentId = user?.id ?? null;

  const prevIdRef = useRef<number | null>(null);

  useEffect(() => {
    const prevId = prevIdRef.current;
    if (prevId === currentId) return;

    if (prevId != null) {
      trackSignOut(buildSignOutPayload({ id: prevId }));
    }
    if (currentId != null && user != null) {
      trackUserProperty(buildSignInPayloadFromUser(user));
    }
    prevIdRef.current = currentId;
    // user reference 가 같은 id 안에서 바뀌어도 발화하지 않도록 deps 에는 currentId 만 포함.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);
}
